"use client";

import * as React from 'react';
import { createClient } from '@/lib/supabase/client';
import type { User } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  authLoading: boolean;
  session: any | null;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

// Кэш для быстрого доступа
const AUTH_CACHE_KEY = 'bastion_auth_cache';
const AUTH_CACHE_TIMESTAMP_KEY = 'bastion_auth_cache_timestamp';
const AUTH_CACHE_TTL = 10 * 60 * 1000; // 10 минут

const LAST_ACTIVE_KEY = 'bastion_last_active_ping';
const LAST_ACTIVE_THROTTLE = 5 * 60 * 1000; // 5 минут

function getCachedAuth(): { user: User; timestamp: number } | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const cached = localStorage.getItem(AUTH_CACHE_KEY);
    const timestamp = localStorage.getItem(AUTH_CACHE_TIMESTAMP_KEY);
    
    if (cached && timestamp) {
      const user = JSON.parse(cached);
      const age = Date.now() - parseInt(timestamp, 10);
      if (age < AUTH_CACHE_TTL) {
        return { user, timestamp: parseInt(timestamp, 10) };
      }
    }
  } catch (e) {
    console.warn('Error reading auth cache:', e);
  }
  
  return null;
}

function setCachedAuth(user: User | null) {
  if (typeof window === 'undefined') return;
  
  try {
    if (user) {
      localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(user));
      localStorage.setItem(AUTH_CACHE_TIMESTAMP_KEY, Date.now().toString());
    } else {
      localStorage.removeItem(AUTH_CACHE_KEY);
      localStorage.removeItem(AUTH_CACHE_TIMESTAMP_KEY);
    }
  } catch (e) {
    console.warn('Error setting auth cache:', e);
  }
}

function updateLastActive(userId: string) {
  if (typeof window === 'undefined') return;
  
  try {
    const lastPing = localStorage.getItem(LAST_ACTIVE_KEY);
    if (lastPing && Date.now() - parseInt(lastPing, 10) < LAST_ACTIVE_THROTTLE) {
      return;
    }
    
    localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
    
    const supabase = createClient();
    supabase
      .from('profiles')
      .update({ last_active_at: new Date().toISOString() })
      .eq('id', userId)
      .then(({ error }) => {
        if (error) console.warn('[AuthProvider] Failed to update last_active_at:', error.message);
      });
  } catch (e) {
    // Ignore errors silently
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(null);
  const [authLoading, setAuthLoading] = React.useState(true);
  const [session, setSession] = React.useState<any | null>(null);
  const subscriptionRef = React.useRef<any>(null);
  const mountedRef = React.useRef(true);
  const initAuthCalledRef = React.useRef(false); // Защита от повторных вызовов

  React.useEffect(() => {
    if (initAuthCalledRef.current) return; // Уже инициализирован
    initAuthCalledRef.current = true;
    
    mountedRef.current = true;
    const supabase = createClient();

    // Сначала проверяем кэш для мгновенного отображения
    const cached = getCachedAuth();
    if (cached?.user) {
      setUser(cached.user);
      setAuthLoading(false);
      updateLastActive(cached.user.id);
    }

    // ЕДИНСТВЕННАЯ подписка на auth события во всем приложении
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!mountedRef.current) return;

      console.log('[AuthProvider] Auth event:', event);

      if (event === 'SIGNED_OUT') {
        setUser(null);
        setSession(null);
        setCachedAuth(null);
        if (mountedRef.current) setAuthLoading(false);
      } else if (event === 'SIGNED_IN' || event === 'USER_UPDATED' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
        if (newSession?.user) {
          setUser(newSession.user);
          setSession(newSession);
          setCachedAuth(newSession.user);
          updateLastActive(newSession.user.id);
          if (mountedRef.current) setAuthLoading(false);
        } else if (event !== 'INITIAL_SESSION') {
          // SIGNED_IN/USER_UPDATED/TOKEN_REFRESHED без сессии — редкий случай.
          const cached = getCachedAuth();
          if (!cached?.user) {
            const { data: { user: fetchedUser } } = await supabase.auth.getUser();
            if (fetchedUser) {
              setUser(fetchedUser);
              setCachedAuth(fetchedUser);
            }
          }
          if (mountedRef.current) setAuthLoading(false);
        }
        // INITIAL_SESSION с пустой сессией — НЕ сбрасываем authLoading здесь.
        // initAuth() ниже сделает реальную проверку через getSession() и
        // только тогда установит финальное состояние. Иначе UI на короткий
        // момент покажет анонимный интерфейс залогиненному пользователю.
      }
    });

    subscriptionRef.current = subscription;

    // Первоначальная проверка сессии - только один раз
    const initAuth = async () => {
      try {
        // Используем кэш если есть, не делаем лишний запрос
        if (cached?.user) {
          // Проверяем сессию только для валидации, не блокируем UI
          supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
            if (!mountedRef.current) return;
            if (currentSession?.user && currentSession.user.id === cached.user.id) {
              // Сессия валидна, обновляем если нужно
              if (user?.id !== currentSession.user.id) {
                setUser(currentSession.user);
                setSession(currentSession);
                setCachedAuth(currentSession.user);
              }
            }
            if (mountedRef.current) {
              setAuthLoading(false);
            }
          });
          return;
        }

        // Если кэша нет, делаем проверку
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        
        if (!mountedRef.current) return;

        if (error) {
          console.error('[AuthProvider] Session error:', error);
          setUser(null);
          setSession(null);
          setCachedAuth(null);
        } else if (currentSession?.user) {
          setUser(currentSession.user);
          setSession(currentSession);
          setCachedAuth(currentSession.user);
        }
      } catch (error) {
        console.error('[AuthProvider] Init error:', error);
        // Используем кэш если есть
        if (mountedRef.current && cached?.user) {
          setUser(cached.user);
        }
      } finally {
        if (mountedRef.current) {
          setAuthLoading(false);
        }
      }
    };

    initAuth();

    // Cleanup
    return () => {
      mountedRef.current = false;
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      initAuthCalledRef.current = false; // Разрешаем повторную инициализацию при размонтировании
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, authLoading, session }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

